import type { OverviewResponse, SystemHealthResponse, AlertsResponse, PipelineResponse, PacingResponse, RunRateResponse, InboxResponse, DomainHealthResponse, DiagnosticsResponse, RefreshResponse, AlertActionResponse } from './types';

const API_BASE = '';
const TIMEOUT = 15_000;

export class ApiError extends Error {
  constructor(msg: string, public status: number, public endpoint: string) { super(msg); this.name = 'ApiError'; }
}

async function apiFetch<T>(path: string, method: 'GET' | 'POST' = 'GET', signal?: AbortSignal, retries = method === 'GET' ? 2 : 0): Promise<T> {
  let lastErr: Error | null = null;
  for (let i = 0; i <= retries; i++) {
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), TIMEOUT);
    if (signal) { if (signal.aborted) { clearTimeout(tid); throw new DOMException('Aborted', 'AbortError'); } signal.addEventListener('abort', () => ctrl.abort(), { once: true }); }
    try {
      const headers: Record<string, string> = {};
      if (method === 'POST') headers['Content-Type'] = 'application/json';
      const res = await fetch(`${API_BASE}${path}`, { method, signal: ctrl.signal, credentials: 'same-origin', headers });
      clearTimeout(tid);
      if (!res.ok) throw new ApiError(`${res.status} ${res.statusText}`, res.status, path);
      return (await res.json()) as T;
    } catch (e) {
      clearTimeout(tid);
      lastErr = e as Error;
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) throw e;
      if (i < retries) await new Promise(r => setTimeout(r, 1000 * (i + 1)));
    }
  }
  throw lastErr ?? new Error(`Failed: ${path}`);
}

export const api = {
  overview: (s?: AbortSignal) => apiFetch<OverviewResponse>('/api/overview', 'GET', s),
  systemHealth: (s?: AbortSignal) => apiFetch<SystemHealthResponse>('/api/system-health', 'GET', s),
  alerts: (s?: AbortSignal) => apiFetch<AlertsResponse>('/api/alerts', 'GET', s),
  pipeline: (s?: AbortSignal) => apiFetch<PipelineResponse>('/api/pipeline', 'GET', s),
  pacing: (s?: AbortSignal) => apiFetch<PacingResponse>('/api/pacing', 'GET', s),
  runRate: (s?: AbortSignal) => apiFetch<RunRateResponse>('/api/run-rate', 'GET', s),
  inbox: (s?: AbortSignal) => apiFetch<InboxResponse>('/api/inbox', 'GET', s),
  domainHealth: (s?: AbortSignal) => apiFetch<DomainHealthResponse>('/api/domain-health', 'GET', s),
  diagnostics: (s?: AbortSignal) => apiFetch<DiagnosticsResponse>('/api/diagnostics', 'GET', s),
  triggerRefresh: (s?: AbortSignal) => apiFetch<RefreshResponse>('/api/refresh', 'POST', s),
  alertAction: (id: string, action: string, s?: AbortSignal) => apiFetch<AlertActionResponse>(`/api/alerts/${id}/${action}`, 'POST', s),
};

export function isDataStale(lastSuccess: string | null): boolean {
  if (!lastSuccess) return true;
  return Date.now() - new Date(lastSuccess.replace(' ', 'T') + 'Z').getTime() > 6 * 3600_000;
}
